/**
 * The settings search MODEL: what a query displays and what a key means.
 *
 * Pure functions and types, no rendering. Split from `settingsRailSearch.tsx`
 * to keep that file under the 300-line extract-first rule in
 * `.agents/rules/frontend-architecture.md` -- the same rule this branch was
 * already corrected on once, and drifted back over while fixing the review.
 *
 * Being pure is the point as much as the line count: `searchKeyAction` and
 * `buildSearchOptions` decide behaviour that is otherwise only observable by
 * driving a real keyboard, so they are worth testing directly.
 */
import type { ComponentType } from "react";

import type { SettingsSearchItem } from "./settingsSearch";

/**
 * Every option a query displays, in the order it is displayed.
 *
 * Sections and rows are one keyboard model, not two. Rendering sections first
 * and then arrowing only through rows meant Enter always opened a row, no key
 * could reach a section, and a section-only query left the combobox reporting
 * itself collapsed while matches sat visible on screen. The active-option index
 * has to address the whole displayed set or it addresses the wrong thing.
 */
export interface SettingsSearchSection {
  readonly icon: ComponentType<{ className?: string }>;
  readonly label: string;
  readonly to: string;
}

export type SettingsSearchOption =
  | { readonly kind: "row"; readonly id: string; readonly item: SettingsSearchItem }
  | { readonly kind: "section"; readonly id: string; readonly section: SettingsSearchSection };

export function buildSearchOptions(
  sections: ReadonlyArray<SettingsSearchSection>,
  rows: ReadonlyArray<SettingsSearchItem>,
): ReadonlyArray<SettingsSearchOption> {
  return [
    ...sections.map(
      (section) =>
        ({ kind: "section", id: `settings-search-section-${section.to}`, section }) as const,
    ),
    ...rows.map(
      (item) => ({ kind: "row", id: `settings-search-result-${item.id}`, item }) as const,
    ),
  ];
}

/** What a keystroke in the search field should do. */
export type SearchKeyAction = "clear" | "ignore" | "next" | "open" | "prev";

/**
 * Decide a keystroke's meaning, separately from carrying it out.
 *
 * The handler was doing both and tripped the complexity gate. Splitting the
 * decision out leaves two things that are each obvious alone -- and puts the
 * IME rule somewhere it cannot be missed: mid-composition every one of these
 * keys belongs to the candidate window, because in Chinese, Japanese and
 * Korean input the arrows choose a candidate and Enter confirms it.
 */
export function searchKeyAction(
  key: string,
  state: { readonly composing: boolean; readonly hasResults: boolean; readonly searching: boolean },
): SearchKeyAction {
  if (state.composing) return "ignore";
  if (key === "Escape") return state.searching ? "clear" : "ignore";
  if (!state.searching || !state.hasResults) return "ignore";
  if (key === "ArrowDown") return "next";
  if (key === "ArrowUp") return "prev";
  if (key === "Enter") return "open";
  return "ignore";
}

/**
 * The result index an arrow key moves to, wrapping at both ends.
 *
 * Extracted so the key handler reads as a list of cases rather than a list of
 * cases plus modular arithmetic -- the two have nothing to do with each other,
 * and together they pushed the handler past the complexity gate.
 */
export function nextResultIndex(
  current: number,
  key: "ArrowDown" | "ArrowUp",
  count: number,
): number {
  const step = key === "ArrowDown" ? 1 : -1;
  return (current + step + count) % count;
}
